import type { CogniteClient, NodeDefinition, NodeOrEdge, QueryRequest } from '@cognite/sdk';
import { z } from 'zod';

import { NO_POS } from '#core/lib/expr/ast.ts';
import { QueryResultTooLargeError } from '#core/lib/expr/errors.ts';
import type { ProviderSource } from '#core/lib/expr/providers/types.ts';
import {
  arrayCol,
  booleanCol,
  type ColumnDoc,
  type ColumnType,
  enumCol,
  hydratedRelationCol,
  IDENTITY_COLUMN_NAMES,
  isIdentityBearing,
  nestedDatasetCol,
  numberCol,
  rowIdentityCol,
  type Schema,
  schemaFromEntries,
  stringCol,
  structCol,
  timestampCol,
  tsRefInnerSchema,
  unknownCol,
  unwrapNullable,
  walkableFields,
  withUnit,
} from '#core/lib/expr/schema/types.ts';
import { type Cell, type Columns, LazyDataset, MaterializedDataset } from '#core/lib/expr/value.ts';
import { resolveSourceUnit, unitForSlug } from '#core/lib/units/registry.ts';
import type { InstanceIdType, TimeSeriesRefType } from '#core/schema/defs.ts';
import { extractTsRef, isTimeSeriesRef, tsRefValueType, type ViewRefShape } from '#core/schema/utils.ts';

import { CDF_INSTANCE_IDENTITY } from './identity.ts';

/**
 * Total-recursion-depth ceiling for {@link deepRemoveDefaults}. A relation
 * list authored as `z.array(z.lazy(() => Target.nullable())).nullish()`
 * unwraps through `optional → nullable → array → lazy → nullable → object`
 * before the element's `{__typename, space, externalId}` shape is reached,
 * so the budget must clear that chain. It also BOUNDS self-referential
 * relations (`CogniteAsset.children: CogniteAsset[]`): at the ceiling the
 * schema is returned as-is, halting an otherwise infinite descent through the
 * cyclic lazy reference. The container walker
 * ({@link zodFieldToColumnType}) only needs the element resolved to its
 * `__typename`-bearing object — it never deep-walks the nested children — so
 * a generous-but-finite ceiling resolves every real view shape while staying
 * cycle-safe.
 */
const DEEP_UNWRAP_MAX_DEPTH = 8;

/**
 * Recursively unwrap a Zod schema so the result's structure reflects
 * the underlying types — defaults, nullable / optional wrappers, array
 * elements, and nested object shapes are walked through. Single call site
 * below (inferring the column schema of a CDF view); lives here rather than
 * a shared util because it relies on Zod internals (`removeDefault`,
 * `unwrap`, `.shape`, `.element`) not in the public type surface. Bounded by
 * {@link DEEP_UNWRAP_MAX_DEPTH} so a self-referential relation schema
 * terminates.
 */
function deepRemoveDefaults(schema: z.ZodTypeAny, depth = 0): z.ZodTypeAny {
  if (depth > DEEP_UNWRAP_MAX_DEPTH) {
    return schema;
  }

  // biome-ignore lint/suspicious/noExplicitAny: Zod internal properties (.shape, ._def, .removeDefault) not in public types
  const schemaAny = schema as any as Record<string, unknown>;

  // ZodDefault wrapper — unwrap to get underlying schema
  if ('removeDefault' in schemaAny && typeof schemaAny.removeDefault === 'function') {
    return deepRemoveDefaults((schemaAny.removeDefault as () => z.ZodTypeAny)(), depth + 1);
  }

  // ZodArray — recursively process the element type. This MUST precede the
  // generic `unwrap` branch: in Zod v4 a `ZodArray` also exposes `.unwrap()`
  // (returning its element), so an `unwrap`-first walk would strip the array
  // wrapper and collapse `z.array(X)` to `walk(X)`, losing the container.
  if (schemaAny.type === 'array' && 'element' in schemaAny && schemaAny.element) {
    return z.array(deepRemoveDefaults(schemaAny.element as z.ZodTypeAny, depth + 1));
  }

  // ZodOptional / ZodNullable / ZodLazy wrappers — recursively unwrap. A lazy
  // resolves to its target schema; the shared depth ceiling above bounds the
  // self-referential relation case so this needs no per-lazy special-casing.
  if ('unwrap' in schemaAny && typeof schemaAny.unwrap === 'function') {
    return deepRemoveDefaults((schemaAny.unwrap as () => z.ZodTypeAny)(), depth + 1);
  }

  // ZodObject — recursively process each field in the shape
  if ('shape' in schemaAny && typeof schemaAny.shape === 'object' && schemaAny.shape !== null) {
    const shape = schemaAny.shape as Record<string, z.ZodTypeAny>;
    const newShape: Record<string, z.ZodTypeAny> = {};
    for (const key in shape) {
      if (!shape[key]) {
        continue;
      }
      newShape[key] = deepRemoveDefaults(shape[key], depth + 1);
    }
    return z.object(newShape);
  }

  // Base case: primitive types or unsupported wrappers remain unchanged
  return schema;
}

export const DataModelSchema = z
  .object({
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_VERSION ?? 'v1'),
    instanceSpace: z.string().prefault(() => import.meta.env.VITE_APP_DM_DEFAULT_INSTANCE_SPACE ?? 'cdf_cdm'),
  })
  .transform(({ externalId, space, version, instanceSpace }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_SPACE`] ?? space,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
    instanceSpace: import.meta.env[`VITE_APP_DM_${externalId.toUpperCase()}_INSTANCE_SPACE`] ?? instanceSpace,
  }));

export interface DataModelInput extends z.input<typeof DataModelSchema> {}
export interface DataModelOutput extends z.output<typeof DataModelSchema> {}

export const DataModelViewSchema = z
  .object({
    type: z.literal('view').default('view'),
    externalId: z.string(),
    space: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_SPACE ?? 'cdf_cdm'),
    version: z.string().prefault(() => import.meta.env.VITE_APP_DM_VIEW_DEFAULT_VERSION ?? 'v1'),
  })
  .transform(({ type, externalId, space, version }) => ({
    externalId,
    space: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_SPACE`] ?? space,
    type,
    version: import.meta.env[`VITE_APP_DM_VIEW_${externalId.toUpperCase()}_VERSION`] ?? version,
  }));

export interface DataModelViewInput extends z.input<typeof DataModelViewSchema> {}
export interface DataModelViewOutput extends z.output<typeof DataModelViewSchema> {}

export type ParsedNode<
  ViewType extends DataModelViewInput,
  SchemaType extends z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
> = z.util.Prettify<z.output<SchemaType> & { _view: ExternalIdValueType; _dataModel: DataModelOutput }>;

export type QueryFn<
  ViewType extends DataModelViewInput,
  _SchemaType extends z.ZodType,
  _ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
  // > = (client: CogniteClient, query: QueryRequest) => Promise<ParsedNode<ViewType, SchemaType, ExternalIdValueType>[]>;
> = (client: CogniteClient, query: QueryRequest) => Promise<NodeDefinition[]>;
/**
 * A computed view column's post-query populator. Runs after `query` returns,
 * over the SAME `NodeDefinition[]` the query produced — input ≡ query output,
 * so computed columns compose as node→node transforms — and writes its column
 * into each node's property bag before decode. Result-set-level: it sees every
 * row, so a column can be derived from siblings (e.g. pairing an ACTUAL series
 * with its TARGET).
 *
 * The built `view` is the sole context: `view.space`/`view.table` locate the
 * property bag. `view.parseNode` is available for reads, but NOT for a column
 * that gates on its own value — the Zod model requires the computed field, so
 * `parseNode` throws while it is still absent; read raw-bag inputs instead.
 *
 * Declared as Zod field metadata: `.meta({ computed: fn })` populates the
 * column; `.meta({ computed: null })` marks it computed-elsewhere (minted in
 * `query`, e.g. the self-series column). Either form excludes the column from
 * CDF pushdown (the `property` proxy returns `null` for it) and strips it from
 * the CDF `select` — it is not a real DMS property, so CDF would 400 on a
 * filter or select that references it.
 */
export type ComputeColumn = (nodes: NodeDefinition[], view: DataModelView) => NodeDefinition[];

export type DataModelView<
  ViewType extends DataModelViewInput = DataModelViewInput,
  SchemaType extends z.ZodType = z.ZodType,
  ExternalIdValueType extends string = ViewType['externalId'] extends string ? ViewType['externalId'] : string,
  SpaceValueType extends string = ViewType['space'] extends string ? ViewType['space'] : string,
> = z.util.Prettify<{
  type: 'view';
  externalId: ExternalIdValueType;
  space: SpaceValueType;
  version: ViewType['version'] extends string ? ViewType['version'] : `v${string}`;
  dataModel: DataModelOutput;
  schema: z.ZodCodec<z.ZodCustom<NodeOrEdge, NodeOrEdge>, SchemaType>;
  table: `${SpaceValueType}/${ExternalIdValueType}`;
  property: { [SchemaKeyType in keyof Required<z.infer<SchemaType>>]: [string, string, string] };
  parseNode: (node: NodeOrEdge) => ParsedNode<ViewType, SchemaType, ExternalIdValueType>;
  query: QueryFn<ViewType, SchemaType, ExternalIdValueType>;
  /**
   * Per-column engine-type overrides applied by {@link viewToSchema} — the seam
   * a view uses to upgrade a column's derived type (e.g. promoting a relation
   * column to a {@link hydratedRelationCol} once its cells are hydrated in
   * `query`). Keyed by column name; absent ⇒ no overrides.
   */
  schemaOverrides?: Readonly<Record<string, ColumnType>>;
  /**
   * Resolve a row's stored unit from its decoded property bag, applied per row
   * to that row's self-series value column. A row has exactly one unit, so this
   * lives on the view — not the column, which could otherwise claim two units
   * for one value stream. The returned string is a source unit string
   * ({@link resolveSourceUnit}): a registered slug/alias or identity compound
   * resolves dimensionally, anything else stamps an opaque display-only unit;
   * blank/`undefined` stamps nothing.
   */
  getUnit?: (props: Record<string, unknown>) => string | undefined;
}>;

/**
 * Extract column names from a view schema based on their Zod type.
 *
 * Useful for identifying specific column types (e.g., timeseries references)
 * without runtime data inference.
 *
 * @param view - Data model view to extract columns from
 * @returns Array of columns with types and references
 */
export function extractViewColumnTypes<ViewType extends DataModelView>(view?: ViewType) {
  if (!view) {
    return undefined;
  }

  // Capture per-field descriptions from the RAW shape before unwrapping.
  // `.describe()` in the view generator is typically called on wrapped types
  // (ZodOptional/ZodNullable from `.nullish()`), so descriptions live on the
  // outer wrapper. `deepRemoveDefaults` recursively unwraps to the inner
  // primitive, dropping the outer wrapper's description — read descriptions
  // BEFORE that unwrap so they survive.
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const rawShape = ((view.schema.out as any)?.shape ?? {}) as Record<string, unknown>;
  const rawDescriptions = new Map<string, string>();
  for (const [name, rawField] of Object.entries(rawShape)) {
    const description = (rawField as { description?: string } | undefined)?.description;
    if (description) {
      rawDescriptions.set(name, description);
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const viewSchemaUnwrapped = deepRemoveDefaults(view.schema.out as any as z.ZodTypeAny);
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const unwrappedAny = viewSchemaUnwrapped as any as Record<string, unknown>;
  const shape = (unwrappedAny.shape ?? {}) as Record<string, unknown>;
  const propertiesList = new Map(
    Object.entries(shape)
      .filter(([name]) => name !== '__typename')
      .map(([name, fieldSchema]) => {
        // Type guard for Zod schema shape with optional __typename. The
        // unwrapped `.shape` is also the structural surface the
        // TimeSeries-ref predicate inspects (`isStep`/`type` fields) —
        // reuse the same handle that reaches `__typename` so the boolean
        // is derived from the type, not the display name.
        //
        // A relation LIST (`z.array(z.object({__typename, …}))`) carries no
        // `.shape` on the array itself — the `__typename` lives on the ARRAY
        // ELEMENT — so resolve the ref-bearing shape from the field's own
        // shape (direct relation / TS-ref) OR, for an array, its element's
        // shape (mirroring the element recursion in `zodFieldToColumnType`).
        const schemaWithShape = fieldSchema as
          | { shape?: ViewRefShape; type?: string; element?: { shape?: ViewRefShape } }
          | undefined;
        const refShape = schemaWithShape?.shape ?? schemaWithShape?.element?.shape;
        const referencedSchema = refShape?.__typename?.value;
        const timeSeriesRef = isTimeSeriesRef(refShape);
        const description = rawDescriptions.get(name);
        // Treat a field as an instance reference when it carries a
        // `__typename` (the usual case) OR when it is structurally a
        // TimeSeries projection — some projections (e.g.
        // `RefineryInventoryTimeSeries`) drop `__typename` entirely, so it
        // cannot be the sole gate.
        return [
          name,
          referencedSchema !== undefined || timeSeriesRef
            ? {
                type: 'reference',
                referencedSchema,
                timeSeriesRef,
                schema: fieldSchema,
                description,
              }
            : { type: schemaWithShape?.type ?? 'unknown', schema: fieldSchema, description },
        ];
      }),
  );
  return propertiesList;
}

/**
 * Project a CDF instance-query node row to its row identity — the
 * `{ space, externalId }` pair that backs the row. Returns `undefined`
 * when either node-level key is missing, which keeps the identity axis
 * row-aligned even for malformed rows. Reads from the SAME node fields
 * that {@link instancesToColumns}/{@link projectCell} project into the
 * `space`/`externalId` columns, so the typed axis and the denormalised
 * columns never drift.
 */
export const nodeIdentity = (node: NodeDefinition): InstanceIdType | undefined => {
  if (typeof node.space === 'string' && typeof node.externalId === 'string') {
    return { space: node.space, externalId: node.externalId };
  }
  return undefined;
};

/**
 * Resolve a view column to its DMS instance-query property selector path —
 * node-level identity (`externalId`/`space`) → `['node', key]`,
 * view-declared properties → `[viewSpace, viewKey, key]`. SSOT extracted
 * from `parseView`'s `.property` proxy, the reference implementation that
 * has classified this correctly from the start. The CDF instance-query
 * fetcher reads the `['node', …]` shape to keep node-level keys out of the
 * wire `properties` selection, which CDF rejects.
 */
export const resolveColumnSelector = (
  view: Pick<DataModelView, 'space' | 'table'>,
  column: string,
): readonly string[] =>
  (IDENTITY_COLUMN_NAMES as readonly string[]).includes(column) ? ['node', column] : [view.space, view.table, column];

/**
 * Drop computed columns from a CDF query's `select` — they are not DMS view
 * properties, so listing one makes CDF 400. `with`/filters are untouched;
 * pushdown already excludes computed names via the view's `property` proxy.
 */
const stripComputedFromSelect = (query: QueryRequest, computed: ReadonlySet<string>): QueryRequest => {
  if (computed.size === 0) {
    return query;
  }
  const select = query.select as Record<string, { sources?: { properties?: string[] }[] }>;
  const cleaned = Object.fromEntries(
    Object.entries(select).map(([key, result]) => [
      key,
      {
        ...result,
        sources: result.sources?.map((source) => ({
          ...source,
          properties: source.properties?.filter((property) => !computed.has(property)),
        })),
      },
    ]),
  );
  return { ...query, select: cleaned as QueryRequest['select'] };
};

export const parseView = <
  const ViewType extends DataModelViewInput,
  const SchemaType extends z.ZodObject | z.ZodPipe<z.ZodObject>,
>({
  view,
  model,
  dataModel,
  query: queryFn,
  schemaOverrides,
  getUnit,
}: {
  view: ViewType;
  model?: SchemaType;
  dataModel?: DataModelInput;
  query?: QueryFn<ViewType, SchemaType>;
  /**
   * Per-column engine-type overrides surfaced on the built view and applied by
   * {@link viewToSchema} — e.g. promoting a relation column to a
   * {@link hydratedRelationCol} once `query` hydrates its cells.
   */
  schemaOverrides?: Readonly<Record<string, ColumnType>>;
  /** Per-row stored-unit resolver — see {@link DataModelView.getUnit}. */
  getUnit?: (props: Record<string, unknown>) => string | undefined;
}): DataModelView<ViewType, SchemaType> => {
  const parsedDataModel = DataModelSchema.parse(dataModel) as DataModelOutput;
  const parsedView = DataModelViewSchema.parse({
    ...view,
    space: view.space ?? parsedDataModel.space,
    version: view.version ?? parsedDataModel.version,
  }) as DataModelView<ViewType, SchemaType>;
  const outputModel = (model ?? z.custom<SchemaType>()) as SchemaType;

  // Collect the set of property names declared on this view, so the
  // `property` proxy below can reject unknown keys instead of silently
  // returning a bogus `[space, table, <unknown>]` tuple that downstream
  // query translators would blindly pass to CDF. Both `ZodObject` (direct
  // `.shape`) and `ZodPipe<ZodObject>` (wrapped, `.out.shape`) are
  // supported. `externalId` and `space` are always valid — they are
  // special-cased below as node-level keys and not part of the schema
  // shape.
  //
  // biome-ignore lint/suspicious/noExplicitAny: Zod internal structure access
  const outputModelAny = outputModel as any;
  const schemaShape = (outputModelAny.shape ?? outputModelAny.out?.shape ?? {}) as Record<string, unknown>;
  const validPropertyNames = new Set<string>([...IDENTITY_COLUMN_NAMES, ...Object.keys(schemaShape)]);

  // Computed columns, declared via Zod field metadata (`.meta({ computed })`):
  // a callback populates the column post-query; `null` marks it computed
  // elsewhere (minted in `query`). Either way the column is excluded from CDF
  // pushdown (the `property` proxy returns `null` for it, which
  // `resolveProperty` maps to a residual, in-memory filter) and stripped from
  // the CDF `select` — it is not a real DMS property, so CDF 400s on any filter
  // or select that references it. The column stays in `validPropertyNames` so
  // the schema and `parseNode` still see it.
  const computedColumns = new Map<string, ComputeColumn | null>(
    Object.entries(schemaShape)
      .map(
        ([name, field]) =>
          [name, ((field as z.ZodType).meta?.() as { computed?: ComputeColumn | null } | undefined)?.computed] as const,
      )
      .filter((entry): entry is [string, ComputeColumn | null] => entry[1] !== undefined),
  );

  const schema = z.codec(z.custom<NodeOrEdge>(), outputModel, {
    // Cast required: Zod codec encode functions don't automatically narrow the constructed
    // object to NodeOrEdge. The structure is valid but TypeScript can't infer it from the
    // dynamic property keys. Runtime validation ensures correctness.
    encode: (value) =>
      ({
        properties: {
          [parsedView.space]: {
            [parsedView.table]: value,
          },
        },
      }) as unknown as NodeOrEdge,
    decode: ({ space, externalId, createdTime, lastUpdatedTime, properties }): z.input<SchemaType> => ({
      space,
      externalId,
      createdTime: new Date(createdTime),
      lastUpdatedTime: new Date(lastUpdatedTime),
      ...(properties?.[parsedView.space]?.[parsedView.table] as z.input<SchemaType>),
    }),
  });

  const parseNode = (node: NodeOrEdge) => {
    const result = schema.safeParse(node);

    if (!result.success) {
      console.warn('Failed to parse node', node, result);
      throw result.error;
    }

    Object.defineProperty(result.data, '_view', { value: parsedView.externalId });

    return result.data;
  };

  const defaultQueryFn: QueryFn<ViewType, SchemaType> = async (client, query) => {
    // TODO: pass QueryClient instance as well, use for caching
    // Page through CDF's cursor so a read larger than one instance-query page
    // returns every match, instead of silently stopping at the first page.
    const rows = (await paginateInstanceQuery(client, query))[view.externalId] ?? [];
    return rows.filter((node) => node.instanceType === 'node');
  };

  // Wrap the query so computed columns are handled uniformly for every view:
  // strip them from the outgoing `select`, then fold their populators over the
  // returned nodes (input ≡ output ≡ `NodeDefinition[]`, so they compose).
  const computedNames = new Set(computedColumns.keys());
  const computeFns = [...computedColumns.values()].filter((fn): fn is ComputeColumn => fn !== null);
  const runQuery: QueryFn<ViewType, SchemaType> = async (client, incoming) => {
    const nodes = await (queryFn ?? defaultQueryFn)(client, stripComputedFromSelect(incoming, computedNames));
    return computeFns.reduce((acc, fn) => fn(acc, parsedView as unknown as DataModelView), nodes);
  };

  return Object.freeze(
    Object.defineProperties(parsedView, {
      dataModel: {
        value: parsedDataModel,
      },
      schema: {
        value: schema,
      },
      table: {
        get() {
          return `${this.externalId}/${this.version}`;
        },
      },
      property: {
        value: new Proxy(
          {},
          {
            get(_target, property) {
              // Let symbol access (Symbol.toPrimitive, Symbol.iterator, etc.)
              // fall through silently — these come from JS engine / debugger
              // introspection and are never legitimate property lookups.
              if (typeof property !== 'string') {
                return undefined;
              }
              if (!validPropertyNames.has(property)) {
                throw new Error(
                  `Property "${property}" does not exist on view "${parsedView.externalId}". ` +
                    `Valid properties: ${[...validPropertyNames].sort().join(', ')}.`,
                );
              }
              // Computed columns have no backing DMS property — signal
              // non-pushable with `null` so `resolveProperty` keeps a filter on
              // them residual instead of pushing a 400-inducing selector.
              if (computedColumns.has(property)) {
                return null;
              }
              return Object.defineProperties(resolveColumnSelector(parsedView, property), {
                _view: { value: parsedView },
                _property: { value: property },
              });
            },
          },
        ),
      },
      parseNode: {
        value: parseNode,
      },
      query: {
        value: runQuery,
      },
      schemaOverrides: {
        value: schemaOverrides,
      },
      getUnit: {
        value: getUnit,
      },
    }),
  );
};

/**
 * The minimal structural surface of a (deep-default-stripped) Zod field the
 * container walker inspects: a `type` tag, an array `element`, the
 * instance-ref / nested-object `shape` (carrying `__typename` for a relation,
 * or the field map for a plain sub-type), an enum's declared `options`, and
 * the Zod v4 `.meta()` accessor (carrying a declared `unit`). All optional —
 * a plain scalar leaf carries only `type`.
 */
type ZodFieldSurface =
  | {
      type?: string;
      element?: unknown;
      shape?: ViewRefShape;
      options?: readonly string[];
      meta?: () => { unit?: string } | undefined;
    }
  | undefined;

/**
 * Map a single (already `deepRemoveDefaults`-unwrapped) Zod field's leaf
 * `type` tag to its scalar engine `ColumnType`. Container kinds (`array`)
 * and references are handled by {@link zodFieldToColumnType} before any
 * tag reaches this table, so a bare `array` tag has no resolvable element
 * to recurse into and falls through to `unknownCol` like any unknown tag.
 */
const ZOD_TAG_TO_LEAF_COLUMN_TYPE: Readonly<Record<string, ColumnType>> = {
  string: stringCol,
  enum: stringCol,
  literal: stringCol,
  number: numberCol,
  int: numberCol,
  float: numberCol,
  bigint: numberCol,
  boolean: booleanCol,
  date: timestampCol,
};

/**
 * The SINGLE source of truth for "view field Zod schema → engine
 * `ColumnType`", owning the leaf-tag table, the reference/TS-ref
 * expansion, and CONTAINER shapes a bare type-tag cannot express. Walks
 * the (already `deepRemoveDefaults`-unwrapped) Zod field:
 *
 *   - `z.array(X)` → `arrayCol(walk(X))`. The walker recurses into the
 *     array ELEMENT so a relation list detects `__typename` on the element,
 *     not on the array — yielding `arrayCol(rowIdentityCol(CDF_INSTANCE_IDENTITY,
 *     target))` for `children`/`equipment`/`files`/`assets` and
 *     `arrayCol(stringCol)` for `aliases`/`tags`.
 *   - an instance-ref element (carries `__typename`, or is structurally a
 *     TimeSeries projection via {@link isTimeSeriesRef}) → a TimeSeries
 *     projection expands to a `nested-dataset` over the 4-column timeseries
 *     inner schema ({@link tsRefInnerSchema}), whose `value` type is read from
 *     the ref's `CogniteTimeSeriesType` discriminator via {@link tsRefValueType}
 *     (`stringCol` for a string-valued series, `numberCol` otherwise); any other
 *     reference is a row identity — {@link rowIdentityCol} over
 *     {@link CDF_INSTANCE_IDENTITY} carrying the `__typename` target view, so
 *     `describeColumnType` renders it `reference<TargetView>`.
 *   - a plain `z.object({...})` that is NOT a reference (no `__typename`, not
 *     a TimeSeries projection) → a `struct` column whose fields are each
 *     walked recursively, so a hand-authored nested sub-type carries its
 *     members' types (units included) end-to-end.
 *   - scalar leaves → a categorical `z.enum` keeps its declared members as an
 *     {@link enumCol}; every other tag maps through the
 *     {@link ZOD_TAG_TO_LEAF_COLUMN_TYPE} table, with `unknownCol` for
 *     unrecognised tags (never throws). A declared `.meta({ unit })` then
 *     stamps the canonical dimension onto the leaf via {@link withUnit};
 *     a slug missing from the registry stamps nothing.
 *
 * `timeSeriesRef` applies only to the TOP-LEVEL field (the per-column
 * TimeSeries projection); nested array elements derive their ref-ness
 * structurally.
 */
export const zodFieldToColumnType = (schema: unknown, timeSeriesRef: boolean): ColumnType => {
  const surface = schema as ZodFieldSurface;
  if (surface?.element !== undefined && surface.element !== null) {
    return arrayCol(
      zodFieldToColumnType(surface.element, isTimeSeriesRef((surface.element as ZodFieldSurface)?.shape)),
    );
  }
  const refShape = surface?.shape;
  const targetView = refShape?.__typename?.value;
  const isRef = targetView !== undefined || isTimeSeriesRef(refShape);
  if (isRef) {
    return timeSeriesRef || isTimeSeriesRef(refShape)
      ? nestedDatasetCol(tsRefInnerSchema(tsRefValueType(refShape)))
      : rowIdentityCol(CDF_INSTANCE_IDENTITY, targetView);
  }
  // A non-reference object is a nested sub-type: recurse each field into a
  // `struct` column. Relation / TS-ref objects are handled above and never
  // reach here, so any remaining `.shape` is a plain field map.
  if (refShape !== undefined) {
    return structCol(
      Object.fromEntries(
        Object.entries(refShape as Record<string, unknown>)
          .filter(([name]) => name !== '__typename')
          .map(([name, field]) => [name, zodFieldToColumnType(field, false)] as const),
      ),
    );
  }
  const leaf =
    surface?.type === 'enum' && surface.options !== undefined && surface.options.length > 0
      ? enumCol(surface.options)
      : (ZOD_TAG_TO_LEAF_COLUMN_TYPE[surface?.type ?? 'unknown'] ?? unknownCol);
  const unit = surface?.meta?.()?.unit;
  return unit === undefined ? leaf : withUnit(leaf, unitForSlug(unit));
};

/**
 * Hard cap on a single knowledge-graph read: the most rows one CDF
 * instance-query may return across all its pages, and the largest an explicit
 * `:limit` may request. The engine pages through {@link CDF_MAX_PAGE_SIZE}-row
 * round-trips up to this ceiling (see `defaultQueryFn`); a read that would
 * overrun it is effectively unbounded and fails loud with
 * `QueryResultTooLargeError` rather than silently truncating. An oversized
 * EXPLICIT `:limit` is still surfaced as a structured `LimitTooLargeError`.
 * Lives here as the single source of truth; both the s-expr engine (the CDF
 * view provider and `expr/ops/knowledge-graph/pushdown.ts`) and any non-engine
 * CDF read paths import from this module.
 */
export const KG_QUERY_MAX_LIMIT = 50000;

/**
 * CDF instance-query per-call page size: the most instances a single
 * `client.instances.query` round-trip requests. A read whose budget exceeds
 * this is split into successive pages walked via the response `nextCursor`
 * (see `defaultQueryFn`). It equals the limit the pre-pagination read already
 * sent, so CDF never rejects a page for an oversized limit. SSOT alongside
 * {@link KG_QUERY_MAX_LIMIT}.
 */
export const CDF_MAX_PAGE_SIZE = 10000;

/**
 * Page a CDF instance query to completeness. `client.instances.query` returns
 * at most {@link CDF_MAX_PAGE_SIZE} instances per call plus a `nextCursor` —
 * which CDF lingers past exhausted data AND drops while rows still remain (right
 * at the requested-limit boundary), so cursor presence is never a reliable "more
 * data" signal in either direction. Each result key is paged independently up to
 * its budget — an explicit `:limit`, or the {@link KG_QUERY_MAX_LIMIT} default —
 * and stops when the budget is filled, when CDF returns an empty page, or when no
 * cursor comes back. Follow-up requests carry only the keys still being paged
 * (trimmed from `with`, `select`, and `cursors`). Overflow is decided on row
 * count alone, never the cursor: an unbounded read (no `:limit`) that reaches the
 * default cap is effectively unbounded and fails loud via
 * {@link QueryResultTooLargeError} rather than truncating silently, even when CDF
 * withholds the trailing cursor; an explicit `:limit` is an intended cap and
 * stops quietly.
 */
export const paginateInstanceQuery = async (
  client: CogniteClient,
  query: QueryRequest,
): Promise<Record<string, NodeOrEdge[]>> => {
  const source = query.with ?? {};
  const sourceSelect = query.select;

  // imperative (CLAUDE.md side-effect carve-out, pre-approved): sequential CDF
  // cursor pagination — each page depends on the prior `nextCursor`, so it can't
  // be a map/reduce. Per-key state accumulates rows across pages; the budget,
  // not the lying cursor, bounds the read.
  const states = Object.keys(source).map((key) => {
    const limit = (source[key] as { limit?: number } | undefined)?.limit;
    return {
      key,
      budget: limit ?? KG_QUERY_MAX_LIMIT,
      explicit: limit !== undefined,
      rows: [] as NodeOrEdge[],
      cursor: undefined as string | undefined,
      active: true,
      overflow: false,
    };
  });

  while (states.some((state) => state.active)) {
    const request = {
      ...query,
      cursors: Object.fromEntries(
        states.filter((state) => state.cursor !== undefined).map((state) => [state.key, state.cursor]),
      ),
      with: Object.fromEntries(
        states.map((state) => {
          const limit = Math.min(state.budget - state.rows.length, CDF_MAX_PAGE_SIZE);
          return [
            state.key,
            {
              ...(source[state.key] ?? {}),
              limit: limit > 0 ? limit : undefined,
            },
          ];
        }),
      ),
      select: Object.fromEntries(
        states
          .filter((state) => state.key in sourceSelect && state.active)
          .map((state) => [state.key, sourceSelect[state.key]]),
      ),
    } as QueryRequest;

    const response = await client.instances.query(request);

    states.forEach((state) => {
      const items = response.items?.[state.key] ?? [];
      state.rows.push(...items);
      const cursor = response.nextCursor?.[state.key];
      // Budget check FIRST: the cursor lies in both directions — CDF lingers it
      // past exhausted data, and also drops it while rows still remain, right at
      // the requested-limit boundary — so it can never gate overflow. Reaching
      // the cap is thus decided on row count alone: an explicit `:limit` is an
      // intended cap and stops quietly; an unbounded read at the cap is
      // effectively unbounded and fails loud.
      if (state.rows.length >= state.budget) {
        state.active = false;
        state.overflow = !state.explicit;
        return;
      }
      // Below budget, an empty page (or absent cursor) is the honest end signal.
      if (items.length === 0 || cursor === undefined) {
        state.active = false;
        return;
      }
      state.cursor = cursor;
    });
  }

  if (states.some((state) => state.overflow)) {
    throw new QueryResultTooLargeError(KG_QUERY_MAX_LIMIT, NO_POS);
  }
  return Object.fromEntries(states.map((state) => [state.key, state.rows]));
};

/**
 * Hard cap on the number of values a cross-dataset set reference may
 * carry when it is resolved from another dataset's column inside a
 * predicate (the `(in externalId (col other-ds externalId))` pattern,
 * shared by the CDF view provider's filter push-down and the s-expr
 * `transform` tool's client-side `filter`). CDF's instance-query `in`
 * filter rejects oversized value lists; surface the limit as a
 * structured `SetRefTooLargeError` so the LLM narrows the upstream query
 * rather than hitting an opaque CDF rejection. SSOT alongside
 * `KG_QUERY_MAX_LIMIT`.
 */
export const SET_REF_MAX = 1000;

/**
 * The non-relation, non-series engine column types of a target view — every
 * column EXCEPT identity-bearing relations ({@link isIdentityBearing}) and
 * per-row timeseries / nested-dataset cells. This is the SSOT field set a
 * HYDRATED relation exposes: the scalars (and plain arrays, e.g. `aliases`) the
 * view's `query` physically denormalises onto each related node. Keyed by
 * column name; both the hydrated-relation TYPE ({@link hydratedRelationFromView})
 * and the wire-level `select` of the hydrating query derive from this ONE map,
 * so exposed ≡ fetched ≡ present.
 */
export const viewScalarColumnTypes = (targetView: DataModelView): Readonly<Record<string, ColumnType>> => {
  const propertiesList = extractViewColumnTypes(targetView);
  if (propertiesList === undefined) {
    return {};
  }
  return Object.fromEntries(
    Array.from(propertiesList).flatMap(([name, descriptor]): [string, ColumnType][] => {
      if (descriptor === undefined) {
        return [];
      }
      const type = zodFieldToColumnType(descriptor.schema, descriptor.timeSeriesRef ?? false);
      const unwrapped = unwrapNullable(type);
      // Drop relations (identity-bearing) and per-row series — only the
      // physically-hydratable scalar / plain-array columns survive.
      if (isIdentityBearing(type) || unwrapped.kind === 'nested-dataset' || unwrapped.kind === 'timeseries') {
        return [];
      }
      return [[name, type]];
    }),
  );
};

/**
 * Build a {@link hydratedRelationCol} for a relation whose cells are physically
 * hydrated to full `targetView` nodes: identity-bearing (its `{space, externalId}`
 * still drives pushdown + identity joins) PLUS the target view's non-relation
 * scalar fields ({@link viewScalarColumnTypes}) as walkable subfields. The field
 * set is derived from the target view — never a hard-coded name list.
 */
export const hydratedRelationFromView = (targetView: DataModelView): ColumnType =>
  hydratedRelationCol(targetView.externalId, viewScalarColumnTypes(targetView), CDF_INSTANCE_IDENTITY);
/**
 * Derive an s-expression engine `Schema` from a CDF
 * {@link DataModelView}. Walks the view's property set via
 * {@link extractViewColumnTypes} and maps each property's Zod field
 * schema to a `ColumnType` via {@link zodFieldToColumnType} — the SSOT
 * for container shapes (`z.array(X)` → `arrayCol(walk(X))`, relation
 * lists → `arrayCol(rowIdentityCol(CDF_INSTANCE_IDENTITY, target))`).
 *
 * The returned `Schema` is the authoritative shape consumed by:
 *
 * - the CDF view provider (behind the generic `query` op) for the
 *   produced `LazyDataset`'s schema field and for `partitionFilter`'s
 *   schema-aware pushability check
 * - any future view-aware op (dashboard tile resolver)
 *
 * `space` and `externalId` are surfaced as plain top-level `stringCol`s
 * — the scalar identity of every node returned by an instance query.
 * Push-down (`pushdown.ts`) translates a leaf on either to the CDF
 * base-property path (`["node","externalId"]` / `["node","space"]`).
 * A cross-dataset RELATION set reference is matched by FULL
 * `{space, externalId}` instance identity: the set-ref pass takes each
 * target's space from the SOURCE dataset's typed identity axis (populated
 * at fetch from these node columns), so the producing and consuming
 * queries need not share a space and no space is ever assumed.
 *
 * Nullable wrappers are NOT inferred here — view-property nullability
 * lives on the raw Zod wrapper before `deepRemoveDefaults` unwraps it,
 * and `extractViewColumnTypes` discards that wrapper to expose the
 * inner tag. If a future need arises, surface nullability through
 * `extractViewColumnTypes` first, then thread it through here.
 *
 * PER-COLUMN OVERRIDES: a view may upgrade specific columns' derived types
 * via `view.schemaOverrides` (set at {@link parseView} time) — e.g. promoting
 * a bare relation column to a {@link hydratedRelationCol} once its cells are
 * physically hydrated in the view's `query`. An override replaces the derived
 * type for that column wherever this SSOT runs (provider schema, pushdown,
 * docs), so exposed ≡ fetched can never drift across call sites.
 */
export const viewToSchema = (view: DataModelView): Schema => {
  const propertiesList = extractViewColumnTypes(view);
  const overrides = view.schemaOverrides ?? {};
  const propertyEntries: readonly [string, ColumnType][] = propertiesList
    ? Array.from(propertiesList).map(([name, descriptor]): [string, ColumnType] => [
        name,
        overrides[name] ??
          (descriptor === undefined
            ? unknownCol
            : zodFieldToColumnType(descriptor.schema, descriptor.timeSeriesRef ?? false)),
      ])
    : [];
  return schemaFromEntries([
    ...IDENTITY_COLUMN_NAMES.map((name): [string, ColumnType] => [name, stringCol]),
    ...propertyEntries,
  ]);
};

/**
 * The ONE producer of agent-facing {@link ColumnDoc}s from a CDF
 * {@link DataModelView}. Walks the view's property set via
 * {@link extractViewColumnTypes} and maps each property's Zod field schema
 * to a canonical engine `ColumnType` through {@link zodFieldToColumnType} —
 * the same SSOT {@link viewToSchema} uses, so the documented type and the
 * executed schema can never drift. `referencedType` carries the related
 * view name for relation and timeseries-reference columns so
 * `describeColumnDocType` can render them by name; a TimeSeries projection
 * whose codegen dropped its `__typename` still names `'CogniteTimeSeries'`
 * so its `timeseries<…>` label survives.
 *
 * When `properties` is supplied, output columns follow that list's order
 * and any name not present in the view is silently skipped. When omitted,
 * every property exposed by {@link extractViewColumnTypes} is emitted in
 * introspection order. Returns `[]` for an undefined view.
 */
export const columnsFromView = (view: DataModelView | undefined, properties?: readonly string[]): ColumnDoc[] => {
  const viewColumns = extractViewColumnTypes(view);
  if (!viewColumns) {
    return [];
  }
  const overrides = view?.schemaOverrides ?? {};
  const names = properties ?? [...viewColumns.keys()];
  return names.flatMap((name): ColumnDoc[] => {
    const descriptor = viewColumns.get(name);
    if (!descriptor) {
      return [];
    }
    // Apply the same per-column overrides {@link viewToSchema} does, so the
    // documented type and the executed schema can never drift.
    const type = overrides[name] ?? zodFieldToColumnType(descriptor.schema, descriptor.timeSeriesRef ?? false);
    const referencedType = descriptor.referencedSchema ?? (descriptor.timeSeriesRef ? 'CogniteTimeSeries' : undefined);
    return [
      {
        name,
        type,
        ...(descriptor.description === undefined ? {} : { description: descriptor.description }),
        ...(referencedType === undefined ? {} : { referencedType }),
      },
    ];
  });
};

/**
 * The single-series cell minting function a view's per-row timeseries
 * cell is built through — structurally the timeseries provider's
 * `buildSource`. Threading it (rather than constructing a source
 * descriptor inline) is the SSOT that keeps a per-row view cell and a
 * top-level `(datapoints)` row cell identical in shape: both go through
 * the one provider, so their inner schema and `native` blob can never
 * drift.
 */
export type MintSeriesSource = (params: { readonly ref: TimeSeriesRefType; readonly valueType?: ColumnType }) => {
  readonly source: ProviderSource;
  readonly schema: Schema;
};

/**
 * Build a per-row timeseries Dataset cell via the timeseries provider's
 * `buildSource`. The cell's inner `value` type is taken from the column's
 * declared inner schema (numeric / string / unknown). The cell is lazy —
 * the datapoints fetch defers until the consumer forces it (e.g.
 * `(unnest …)` or a per-cell `time-bucket`).
 */
const tsRefCell = (mintSeries: MintSeriesSource, ref: TimeSeriesRefType, innerSchema: Schema, unit?: string): Cell => {
  const base = innerSchema.get('value');
  // Per-row unit: stamp the row's resolved source unit onto its own value
  // column. The inner value type is otherwise the schema's static declaration,
  // shared across rows — but each row is its own nested dataset, so the unit
  // is row-local. Resolution never drops a non-blank unit (worst case it goes
  // opaque display-only); a blank string stamps nothing (`withUnit` no-op).
  const valueType = unit !== undefined && base !== undefined ? withUnit(base, resolveSourceUnit(unit)) : base;
  const { source, schema } = mintSeries({ ref, valueType });
  return new LazyDataset({ source, ops: [] }, schema) as Cell;
};

/**
 * Project a single CDF node row's value at `colName` to a Cell. CDF nests
 * declared view properties under `properties[viewSpace][viewKey][prop]`;
 * node-metadata fields (`createdTime`, `lastUpdatedTime`) and the scalar
 * identity columns (`space`, `externalId`) live at the top level.
 *
 * Type-driven branches:
 *   - `nested-dataset` columns (today: CogniteTimeSeries properties via
 *     `viewToSchema`) project to a `LazyDataset` minted by the timeseries
 *     provider (`mintSeries`) so downstream consumers can `(unnest …)` to
 *     flatten the inner `{timestamp, value, isString}` rows on
 *     demand without forcing the datapoints fetch up-front. The CDF
 *     reference shape is `{space, externalId}` (or a bare externalId
 *     string); we capture both via `extractTsRef` so the provider can
 *     disambiguate two timeseries that share an externalId across spaces.
 *   - `hydrated-relation` columns (and arrays of them) project the raw
 *     related-node bag the view's `query` stuffed into the property to the
 *     exact walkable surface the schema declares — identity ∪ hydrated
 *     fields per {@link walkableFields}, null-filled — so a declared
 *     subfield is present on every cell even when CDF omitted the
 *     property, and per-cell `(col …)` walks can never miss.
 */
export const projectCell = (
  row: NodeDefinition,
  colName: string,
  colType: ColumnType,
  viewSpace: string,
  viewKey: string,
  mintSeries: MintSeriesSource,
  getUnit?: (props: Record<string, unknown>) => string | undefined,
): Cell => {
  if (colName === 'space') {
    // A node's instance space is only ever the value CDF returned; never
    // fabricate one (a `''` fallback would forge an instance identity). A
    // missing space yields null, so identity synthesis omits it rather than
    // inventing it.
    return (typeof row.space === 'string' ? row.space : null) as Cell;
  }
  if (colName === 'externalId') {
    return (typeof row.externalId === 'string' ? row.externalId : null) as Cell;
  }
  if (colName === 'createdTime') {
    return row.createdTime as Cell;
  }
  if (colName === 'lastUpdatedTime') {
    return row.lastUpdatedTime as Cell;
  }
  const propValue = row.properties?.[viewSpace]?.[viewKey]?.[colName];
  if (colType.kind === 'nested-dataset') {
    // SSOT: accept BOTH CDF encodings (bare externalId string OR
    // {space, externalId} object) via extractTsRef, and build the
    // cell only through tsRefCell — so a column the schema typed
    // nested-dataset can never project to a non-Dataset cell.
    const ref = extractTsRef(propValue);
    if (ref === null) {
      return null;
    }
    const unit = getUnit?.((row.properties?.[viewSpace]?.[viewKey] ?? {}) as Record<string, unknown>);
    return tsRefCell(mintSeries, ref, colType.innerSchema, unit);
  }
  const relationType = unwrapNullable(colType);
  const elementType = relationType.kind === 'array' ? unwrapNullable(relationType.element) : relationType;
  if (elementType.kind === 'hydrated-relation') {
    // SSOT: shape the cell to EXACTLY the walkable surface the schema
    // declares (identity ∪ hydrated fields, via walkableFields), null-filling
    // keys the bag omits. The bag the view's `query` stuffed is the raw CDF
    // property set — it drops unset optional properties, and a declared
    // walkable the cell lacks would blow up the strict per-cell `(col …)`
    // walk. Shaping here — where every cell is constructed — keeps
    // exposed ≡ fetched ≡ present true by construction for every hydrating
    // view, and drops undeclared keys so the cell never leaks extra fields.
    const fieldNames = Object.keys(walkableFields(elementType) ?? {});
    const shapeCell = (bag: unknown): Cell => {
      if (bag === null || bag === undefined || typeof bag !== 'object') {
        return null;
      }
      return Object.fromEntries(
        fieldNames.map((field) => [field, ((bag as Record<string, unknown>)[field] ?? null) as Cell]),
      ) as Cell;
    };
    if (relationType.kind === 'array') {
      return Array.isArray(propValue) ? (propValue.map(shapeCell) as Cell) : null;
    }
    return shapeCell(propValue);
  }
  if (propValue === undefined || propValue === null) {
    return null;
  }
  // A CDF property can come back as "" for a column `viewToSchema`
  // declared numeric; left intact it violates the numeric column type
  // (degrading the column to `unknown` and emptying downstream
  // aggregates/sorts) — the same hazard the ts-ref `seriesToColumns`
  // projection guards against. String columns keep "" verbatim.
  if (propValue === '' && unwrapNullable(colType).kind === 'number') {
    return null;
  }
  return propValue as Cell;
};

/**
 * Project CDF instance-query node rows into a column-major `Columns` shape
 * matching the engine `Schema` for the view. Iterates the schema's column
 * order so every column reaches the same row count even when CDF omits
 * unset optional properties from the response.
 *
 * `mintSeries` is the timeseries provider's `buildSource` — every per-row
 * timeseries cell is minted through it, so a view's nested datapoints cell
 * and a top-level `(datapoints)` row cell share one construction path.
 */
export const instancesToColumns = (
  rows: readonly NodeDefinition[],
  schema: Schema,
  view: DataModelView,
  mintSeries: MintSeriesSource,
): Columns => {
  const viewSpace = view.space;
  const viewKey = `${view.externalId}/${view.version}`;
  // Capture `(name, colType)` once per schema column — projectCell's
  // type-driven branches (nested-dataset → LazyDataset) read colType
  // and the cost is per-row otherwise.
  const colEntries = Array.from(schema.entries());
  const columns: Record<string, Cell[]> = Object.fromEntries(colEntries.map(([name]) => [name, [] as Cell[]]));
  for (const row of rows) {
    for (const [name, colType] of colEntries) {
      columns[name]?.push(projectCell(row, name, colType, viewSpace, viewKey, mintSeries, view.getUnit));
    }
  }
  return columns;
};
/**
 * SSOT: materialise instance-query node rows to a MaterializedDataset with its
 * typed identity axis populated, so the axis never drifts from the projected
 * space/externalId columns.
 */
export const materializeInstanceRows = (
  rows: readonly NodeDefinition[],
  schema: Schema,
  view: DataModelView,
  mintSeries: MintSeriesSource,
): MaterializedDataset =>
  new MaterializedDataset(instancesToColumns(rows, schema, view, mintSeries), schema, rows.map(nodeIdentity));
